import { Body, Button, Heading, Icon } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { getIn, useFormikContext } from 'formik';
import React, { useContext, useMemo } from 'react';

import Autocomplete from 'src/components/Autocomplete';
import { CrossIcon, PlusIcon } from 'src/components/icons';
import { Edit } from 'src/components/icons/EditIcon';
import { MenuAction } from 'src/components/MenuAction';
import {
  GroupListboxComponent,
  RenderGroup,
} from 'src/components/select/GroupListBox';
import { TextField } from 'src/components/TextField';
import { Action } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { ActionButtonModal } from 'src/legacy/components/Modals';
import {
  CREATE_NEW_PRODUCT_ITEM_ID,
  LineItemFormSchemaExtended,
  LineItemOptions,
} from 'src/routes/payments/components/lineItem-editor/lineItem/Controller';
import { LineItemEditorPricingMode } from 'src/routes/payments/components/lineItem-editor/LineItemEditor';
import { PresentationProps } from 'src/routes/payments/components/lineItem-editor/types';
import { PaymentFileDropzone } from 'src/routes/payments/components/PaymentFileDropzone';
import { PaymentRichTextEditor } from 'src/routes/payments/components/PaymentRichTextEditor';
import { ProductStoreFeatures } from 'src/routes/payments/store/form/components/ProductStoreFeatures';
import { ProductStoreSchema } from 'src/routes/payments/store/types/schema';
import { LineItemType } from 'src/services/api/invoicesApi';
import { BillingPeriodMapper } from 'src/services/api/productsApi';
import {
  GetDefaultCurrency,
  StripeCurrencyFormatterAdapter,
} from 'src/utils/PaymentsUtils';

const NO_OPTION = 'no_option';

type ProductItem = {
  id: string;
  name: string;
  amount?: string;
  interval?: string;
};

enum MoveDirection {
  Up = 'up',
  Down = 'down',
}

export const NewProductOptionRenderer = ({
  id,
  name,
  amount,
  interval,
}: ProductItem) => {
  if (id === CREATE_NEW_PRODUCT_ITEM_ID) {
    return (
      <div className="flex justify-start items-center overflow-hidden">
        <PlusIcon style={{ fontSize: 10 }} />
        <Body className="ml-2 overflow-hidden whitespace-nowrap overflow-ellipsis mr-5">
          Create service
        </Body>
      </div>
    );
  }
  if (id === NO_OPTION) {
    return (
      // no-option is a disabled option, and thus gets 0.6 opacity
      <div className="flex items-center justify-center">
        <Body className="text-black text-[12px] opacity-100">
          {`No product found`}
        </Body>
      </div>
    );
  }
  return (
    <>
      <div className="flex justify-between items-center w-full gap-2">
        <Body className=" truncate whitespace-nowrap overflow-hidden pr-2 ">
          {name}
        </Body>
        <Body className="text-[10px] text-gray-small text-right whitespace-nowrap">
          {StripeCurrencyFormatterAdapter({
            value: Number(amount),
            currency: GetDefaultCurrency(),
          })}
          {interval && ` / ${BillingPeriodMapper[interval]}`}
        </Body>
      </div>
    </>
  );
};

export function ProductStorePresentation<AllowRecurringPrices extends boolean>({
  fieldName,
  itemIndex,
  productDropdownOptions,
  onLineItemSelection,
  onFilterOptions,
  pricesDropdownOptions,
  onRemoveLineItem,
  lineItem,
  allowRecurringPrices,
  onSaveItem,
  onDiscardItem,
  onClearItem,
  onEditItem,
  canBeRemoved,
  productStoreImageUrl,
  placeholder,
  description,
  onUpdateTipTapEditor,
  lineItems,
  allowCoverImage,
  mode,
  isItemEditing,
  allowFeature,
  onSetActiveFormSection,
  onEditDraftFeature,
  onOpenCreateProductPopover,
  isDragging,
}: PresentationProps<AllowRecurringPrices> & {
  productStoreImageUrl?: string;
  description: string;
  onUpdateTipTapEditor: (editor: Editor | null) => void;
  allowCoverImage: boolean;
  allowFeature: boolean;
  placeholder: string;
  isItemEditing?: boolean;
  onSetActiveFormSection: () => void;
  lineItem: LineItemFormSchemaExtended;
  onEditDraftFeature?: (feature: string[] | undefined) => void;
  lineItems: LineItemFormSchemaExtended[];
  onOpenCreateProductPopover: (
    itemIndex: number,
    productId?: string,
    priceId?: string,
  ) => void;
  isDragging?: boolean;
}) {
  const { errors, touched, handleBlur, handleChange, setFieldValue, values } =
    useFormikContext<ProductStoreSchema>();
  const descriptionFieldName = `${fieldName}.description`;
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  const isEditingFieldName = `${fieldName}.isEditing`;
  const nameFieldName = `${fieldName}.name`;
  const featuresFieldName = `${fieldName}.features`;
  const productIdFieldName = `${fieldName}.productId`;
  const priceId = `${fieldName}.priceId`;
  const touchFieldsName = getIn(touched, nameFieldName);
  const errorName = getIn(errors, nameFieldName);
  const errorFeature = getIn(errors, featuresFieldName);
  const accountInfo = useAppSelector((state) => state.payments.accountInfo);
  const defaultCurrency = accountInfo?.default_currency || GetDefaultCurrency();
  const portalConfig = useContext(PortalConfigContext);
  const [draftDescription, setDraftDescription] = React.useState({
    description: description || '',
  });

  const selectedProductItem = {
    id: lineItems[itemIndex].id,
    name: lineItems[itemIndex].name || '',
    type: lineItems[itemIndex].type,
  };

  const sortedPriceList = useMemo(() => {
    return [...pricesDropdownOptions].sort((a, b) => a.amount - b.amount);
  }, [pricesDropdownOptions]);

  const interval =
    lineItem.type === LineItemType.Product &&
    sortedPriceList.find((price) => price.ref === lineItem?.priceId)?.interval;

  const isSubscriptionStore =
    mode === LineItemEditorPricingMode.subscriptionsStore;

  const updatedItems = isSubscriptionStore
    ? [...values.subscriptions]
    : [...values.oneTimeProducts];

  const image = productStoreImageUrl
    ? decodeURIComponent(productStoreImageUrl.split('/')?.pop() || '')
    : '';

  const getMoveIndex = (direction: MoveDirection) => {
    const targetIndex =
      direction === MoveDirection.Up ? itemIndex - 1 : itemIndex + 1;
    const updatedProductItems = [...updatedItems];
    [updatedProductItems[itemIndex], updatedProductItems[targetIndex]] = [
      updatedProductItems[targetIndex],
      updatedProductItems[itemIndex],
    ];

    setFieldValue(
      isSubscriptionStore ? 'subscriptions' : 'oneTimeProducts',
      updatedProductItems,
    );
  };

  const handleMoveUp = () => {
    if (itemIndex <= 0) return;
    getMoveIndex(MoveDirection.Up);
  };
  const handleMoveDown = () => getMoveIndex(MoveDirection.Down);

  const handleEditItem = () => {
    const updatedItem = {
      ...updatedItems[itemIndex],
    };
    if (onEditDraftFeature) {
      onEditDraftFeature(
        values.subscriptions[itemIndex]?.features?.filter(Boolean),
      );
    }
    setDraftDescription({
      description: updatedItem.description || '',
    });
    onEditItem();
    onSetActiveFormSection();
  };

  const handleEditProductItem = () => {
    if (lineItem.type === LineItemType.Product && lineItem.productId) {
      onOpenCreateProductPopover(
        itemIndex,
        lineItem.productId,
        lineItem.priceId,
      );
    }
  };

  const handleRemoveListingModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseRemoveListing = () => {
    setOpenDeleteModal(false);
  };

  const actions = useMemo(() => {
    const baseActions: Action[] = [
      {
        name: 'Edit listing',
        icon: <Edit style={{ fontSize: 13 }} />,
        onClick: handleEditItem,
      },
    ];

    return [
      ...baseActions,
      ...(itemIndex > 0
        ? [
            {
              name: 'Move up',
              icon: <Icon icon="ArrowUpSolid" className="w-[13px] h-[13px]" />,
              onClick: () => {
                handleMoveUp();
              },
            },
          ]
        : []),
      ...(itemIndex < lineItems.length - 1
        ? [
            {
              name: 'Move down',
              icon: (
                <Icon icon="ArrowDownSolid" className="w-[13px] h-[13px]" />
              ),
              onClick: () => {
                handleMoveDown();
              },
            },
          ]
        : []),
      {
        name: 'Remove listing',
        icon: <CrossIcon style={{ fontSize: 13 }} />,
        onClick: () => {
          handleRemoveListingModal();
        },
        isDelete: true,
      },
    ];
  }, [handleEditItem, itemIndex, lineItems, handleMoveUp, handleMoveDown]);

  const productActionsMenu = useMemo(() => {
    const baseActions: Action[] = [
      {
        name: 'Edit service',
        icon: <Edit style={{ fontSize: 13 }} />,
        onClick: () => {
          handleEditProductItem();
        },
      },
    ];
    return baseActions;
  }, [handleEditProductItem]);

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    if (lineItem.type === LineItemType.Product && lineItem.productId) {
      setFieldValue(
        `${fieldName}.name`,
        productDropdownOptions.find(
          (option) => option.id === lineItem.productId,
        )?.name,
      );
    }
  };

  const shouldHideProductField = useMemo(() => {
    return !lineItem.isValid && !lineItem.isEditing;
  }, [lineItem.isValid, lineItem.isEditing]);

  const handleContentChange = (editor: Editor) => {
    const content = editor.getHTML();
    setFieldValue(`${fieldName}.description`, content);
  };

  return (
    <div key={lineItem.id}>
      {lineItem.isEditing ? (
        <div>
          <Heading className="text-lg text-dark-black pb-4">
            Service details
          </Heading>
          <Heading className="text-black-heading mb-1">Service</Heading>
        </div>
      ) : null}
      <div className="w-full flex flex-col gap-4 @container">
        {shouldHideProductField ? (
          <div
            className={clsx('w-full', {
              // When the "Add Service" button is clicked, the autocomplete field should appear below the existing service list.
              // Because both the service list and the autocomplete are rendered inside the same loop,
              // we use absolute positioning to correctly place the autocomplete outside the list.
              'absolute top-5': !lineItem.isEditing && lineItems.length > 1,
            })}
          >
            <Autocomplete
              freeSolo
              onFocus={onSetActiveFormSection}
              classes={{
                root: 'group',
                paper:
                  'border border-solid border-divider-card shadow-popover-050',
                listbox: 'py-1 max-h-[295px] !h-auto !overflow-y-scroll',
                option:
                  'px-3 bg-white hover:bg-gray-100 focus:bg-gray-10 min-h-8',

                inputRoot:
                  ' h-[38px] [&>input]:text-[14px] [&>div>button>span]:text-black-heading [&>input]:font-normal',
                clearIndicator: clsx({
                  'group-hover:visible': canBeRemoved,
                  invisible: !canBeRemoved,
                }),
                popupIndicator: 'hidden',
              }}
              onChange={(event, value) => {
                onLineItemSelection(event, value);
                if (
                  value &&
                  typeof value === 'object' &&
                  'id' in value &&
                  value.id === LineItemOptions.CreateNewProductItem
                ) {
                  onOpenCreateProductPopover(itemIndex);
                }
              }}
              getOptionDisabled={(option) => option.id === NO_OPTION}
              renderOption={(params) => (
                <NewProductOptionRenderer {...params} />
              )}
              onInputChange={(_, __, reason) => {
                if (reason === 'clear') {
                  if (lineItem.isEditing) {
                    onClearItem();
                    setFieldValue(productIdFieldName, '');
                    setFieldValue(priceId, '');
                    setFieldValue(featuresFieldName, []);
                    setFieldValue(isEditingFieldName, false);
                  } else {
                    onRemoveLineItem();
                  }
                }
              }}
              openOnFocus={Boolean(
                itemIndex === 0
                  ? false
                  : lineItem.type === LineItemType.Product &&
                      !lineItem.productId,
              )}
              ListboxComponent={
                GroupListboxComponent as React.ComponentType<
                  React.HTMLAttributes<HTMLElement> & {
                    itemHeight?: number | ((index: number) => number);
                  }
                >
              }
              ListboxProps={{
                itemHeight: (index: number) => (index < 1 ? 36 : 32),
              }}
              renderGroup={RenderGroup}
              options={productDropdownOptions}
              getOptionLabel={(option) => option.name}
              inputValue={selectedProductItem.name}
              filterOptions={onFilterOptions}
              renderInput={(params) => (
                <TextField
                  autoFocus={Boolean(
                    itemIndex === 0
                      ? false
                      : lineItem.type === LineItemType.Product &&
                          !lineItem.productId,
                  )}
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off',
                    isautocomplete: 'true',
                  }}
                  error={Boolean(touchFieldsName && errorName)}
                  helperText={touchFieldsName && errorName ? errorName : ''}
                  name={nameFieldName}
                  onBlur={handleOnBlur}
                  fullWidth
                  variant="outlined"
                  placeholder={placeholder}
                  onChange={handleChange}
                />
              )}
              groupBy={(option) =>
                option.type === 'product' ? 'Existing services' : ''
              }
            />
          </div>
        ) : (
          <>
            {lineItem.isEditing ? (
              <div className="flex justify-between items-start border border-non-hover-border border-solid rounded p-3">
                <div className="flex flex-col gap-1">
                  <Body className="overflow-hidden text-ellipsis whitespace-nowrap truncate w-full max-w-[224px] @xs:max-w-[280px] @sm:max-w-[330px] @md:max-w-[416px]">
                    {lineItem.name}
                  </Body>
                  <div className="flex items-center gap-2">
                    <Body size="sm" className="text-gray-small">
                      {StripeCurrencyFormatterAdapter({
                        value: Number(lineItem.amount) * 100,
                        currency: defaultCurrency,
                      })}
                      {allowRecurringPrices &&
                        interval &&
                        ` / ${BillingPeriodMapper[interval]}`}
                    </Body>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <MenuAction actions={productActionsMenu} />
                </div>
              </div>
            ) : (
              <div
                className={clsx(`flex justify-between items-center p-3`, {
                  'opacity-25 pointer-events-none':
                    isItemEditing && !lineItem.isEditing,
                })}
              >
                <Body
                  className={clsx(
                    `overflow-hidden text-ellipsis whitespace-nowrap truncate w-full max-w-[224px] @xs:max-w-[280px] @sm:max-w-[330px] @md:max-w-[416px]"`,
                    {
                      'text-gray-300': isDragging,
                    },
                  )}
                >
                  {lineItem.name}
                </Body>
                <div className="flex gap-4 items-center">
                  <MenuAction actions={actions} />
                </div>
              </div>
            )}
          </>
        )}

        {lineItem.isEditing && (
          <>
            {allowCoverImage && (
              <div>
                <Heading className="text-[14px] text-dark-black mb-1">
                  Cover image
                </Heading>
                <PaymentFileDropzone
                  imageUrl={productStoreImageUrl || ''}
                  image={image}
                  portalConfig={portalConfig}
                  basePath={'images/product-store'}
                  formikImagefieldName={`oneTimeProducts[${itemIndex}].imageUrl`}
                  formikManualImageFieldName={`oneTimeProducts[${itemIndex}].isImageUrlSetManually`}
                />
              </div>
            )}
            <div
              className={clsx({
                'order-2 [&>div]:py-2 ': isSubscriptionStore,
                'order-3': !isSubscriptionStore,
              })}
            >
              <Heading className="text-[14px] text-dark-black mb-1">
                Description
              </Heading>
              <PaymentRichTextEditor
                description={description}
                onContentChange={handleContentChange}
                onUpdateTipTapEditor={onUpdateTipTapEditor}
                className={clsx({
                  '!min-h-0 !mt-0 [&>p]:!leading-[20px]': isSubscriptionStore,
                })}
              />
            </div>
            {allowFeature && (
              <>
                <div className="order-4">
                  <Heading className="text-[14px] text-dark-black mb-1">
                    Features
                  </Heading>
                  <ProductStoreFeatures
                    itemIndex={itemIndex}
                    formikFeatureFieldName={featuresFieldName}
                  />
                </div>
              </>
            )}

            <div className="flex gap-2 items-center order-5">
              <Button
                onClick={() => {
                  // We handle error for feature here because features
                  // are specific to product store line items.
                  // Rest of the errors are handled in LineItem controller component
                  if (!errorFeature) {
                    // We filter out empty features here
                    // because we don't want to save empty features

                    const updatedItem = {
                      ...updatedItems[itemIndex],
                      features: (
                        updatedItems[itemIndex]?.features || []
                      ).filter(
                        (f) => typeof f === 'string' && f.trim().length > 0,
                      ),
                    };
                    updatedItems[itemIndex] = updatedItem;
                    setFieldValue(
                      isSubscriptionStore ? 'subscriptions' : 'oneTimeProducts',
                      updatedItems,
                    );

                    onSaveItem();
                  }
                }}
                variant="primary"
                label="Save"
                className="w-20"
                type="button"
              />

              <Button
                onClick={() => {
                  onDiscardItem();
                  setFieldValue(
                    descriptionFieldName,
                    draftDescription.description,
                  );
                }}
                label="Cancel"
                variant="secondary"
                className="w-20"
              />
            </div>
          </>
        )}
        {openDeleteModal && (
          <ActionButtonModal
            open={openDeleteModal}
            onClose={handleCloseRemoveListing}
            positiveAction
            onPositiveActionPerformed={onRemoveLineItem}
            title={'Remove listing?'}
            positiveActionText="Remove"
            cancelActionText={'Cancel'}
            mainComponent={
              'This listing will be removed from your Store. You can add it back at any time.'
            }
            isDeleteModal
          />
        )}
      </div>
    </div>
  );
}
